import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {queries} from "shared/api/queries";
import "./planTable.css";
import {useDataAll} from "shared/hooks/useDataAll";
import {cn, formatDate, getApiErrorMsg} from "shared/lib/fns";
import {IPlanLongItem, MapSearchParamKey, PlanSearchParamKey} from "features/plan/types";
import {useTableSelection} from "features/plan/hooks/useTableSelection";
import {useSearchParams} from "react-router-dom";
import {SelectableTable} from "features/plan/components/filter/SelectableTable";

type Props = {
  onSelectData: (data: IPlanLongItem[]) => void;
};

type Column = {
  key: string;
  title: string;
  width?: string;
  className?: string;
  render: (row: IPlanLongItem) => React.ReactNode;
};

export interface PlanTableRef {
  refresh: () => void;
}

const CHECKBOX_COLUMN_WIDTH = "40px";
const KEY = "id"
export const PlanTable = forwardRef<PlanTableRef, Props>(({onSelectData}: Props, ref) => {
  const {t} = useTranslation(["shipping"]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const shouldSelectAllOnNextDataRef = useRef(true);
  const prevQueryParamsRef = useRef<string | null>(null);
  const [params] = useSearchParams({plan_date: formatDate(new Date())})
  const hasSelectedSectorParams = !!params.get(PlanSearchParamKey.clusterIdStartswithInCsv);
  const queryParams = useMemo(() => {
    const nextParams = new URLSearchParams(params);
    
    nextParams.delete(MapSearchParamKey.mapCenterLat);
    nextParams.delete(MapSearchParamKey.mapCenterLng);
    nextParams.delete(MapSearchParamKey.mapZoom);
    
    return nextParams.toString();
  }, [params]);
  
  const {
          listData, mutate, isLoading, isValidating, error
        } = useDataAll<IPlanLongItem>({
    url: hasSelectedSectorParams ? queries.plan.long : null,
    params: queryParams,
    options: {
      initialSize: 1,
      revalidateOnFocus: false,
      revalidateIfStale: true,
      revalidateFirstPage: true,
      revalidateOnMount: true,
      dedupingInterval: 0,
    },
  });
  
  useImperativeHandle(ref, () => ({
    refresh: () => {
      mutate();
    },
  }), [mutate]);

  useEffect(() => {
    if (prevQueryParamsRef.current === null) {
      prevQueryParamsRef.current = queryParams;
      return;
    }

    if (prevQueryParamsRef.current !== queryParams) {
      shouldSelectAllOnNextDataRef.current = true;
      prevQueryParamsRef.current = queryParams;
    }
  }, [queryParams]);
  
  useEffect(() => {
    if (!listData) {
      return;
    }
    
    if (hasSelectedSectorParams && shouldSelectAllOnNextDataRef.current) {
      setSelectedRowKeys(listData.map((row) => String(row[KEY])));
      shouldSelectAllOnNextDataRef.current = false;
      return;
    }
    
    setSelectedRowKeys((prevKeys) => prevKeys.filter((key) => listData.some((row) => String(row[KEY]) === key)));
  }, [hasSelectedSectorParams, listData]);
  
  useEffect(() => {
    if (!listData) {
      return;
    }
    onSelectData(listData.filter((row) => selectedRowKeys.includes(String(row[KEY]))));
  }, [listData, onSelectData, selectedRowKeys]);
  
  const handleChangeSelectedRowKeys = useCallback((keys: string[]) => {
    onSelectData(listData.filter((row) => keys.includes(String(row[KEY]))));
    setSelectedRowKeys(keys);
  }, [listData, onSelectData]);
  
  const {
          selectedKeySet, isAllSelected, isPartiallySelected, handleToggleAll, toggleRow,
        } = useTableSelection({
    rows: listData,
    selectedRowKeys,
    getRowKey: (row) => String(row[KEY]),
    onChangeSelectedRowKeys: handleChangeSelectedRowKeys,
  });
  
  const columns = useMemo<Column[]>(() => [
    {
      key: "cluster_id",
      title: t("cluster id"),
      render: (row) => (<span className={"planCluster planCell"}>{row.cluster_id || "-"}</span>),
    }, {
      key: "bunny_type", title: t("bunny type"), width: "1fr", render: (row) => {
        const bunnyColor = String(row.bunny_color).toUpperCase();
        
        return (<span className={cn("bunnyType ", bunnyColor)}>
            {bunnyColor || "-"}
          </span>);
      },
    }, {
      key: "chute_id", title: t("chute id"), width: "0.9fr", render: (row) => row.chute_id || "-",
    }, {
      key: "shipping_count",
      title: t("shipping count"),
      width: "1fr",
      render: (row) => `${row.count_scanned_all} / ${row.shipping_item_count}`,
    },
  ], [t]);
  
  const gridTemplateColumns = `${CHECKBOX_COLUMN_WIDTH} ${columns.map((column) => column.width || "1fr")
                                                                 .join(" ")}`;
  
  return (<div
    className={"plan-table"}>
    <div className={"min-h-0 flex-1 overflow-auto"}>
      <SelectableTable
        rows={listData}
        columns={columns}
        getRowKey={(row) => String(row[KEY])}
        isLoading={isLoading || isValidating}
        error={error}
        errorLabel={error ? getApiErrorMsg(error) : undefined}
        gridTemplateColumns={gridTemplateColumns}
        selectedKeySet={selectedKeySet}
        isAllSelected={isAllSelected}
        isPartiallySelected={isPartiallySelected}
        onToggleAll={handleToggleAll}
        onToggleRow={toggleRow}
      />
    </div>
  </div>);
});
